import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { BillingCurrencySchema, ReportedCurrencySchema } from '../currency/currency';
import { EntitlementStoreSchema } from '../entitlement-store/entitlement-store';
import { FeePurposeSchema } from '../fee-purpose/fee-purpose';
import { MoneyAmountSchema } from '../money/money';
import { ProductKeySchema } from '../product-key/product-key';

/**
 * A fee payment is either settled or refunded — there is no pending state.
 *
 * The pending half lives where the money is still moving: a direct order sits
 * `awaiting_confirmation` in `direct_payment_orders`, and a store purchase does
 * not exist for this platform until RevenueCat's webhook says it landed. The
 * ledger row is written the moment either door settles, so a row here **is** a
 * paid fee, and consumers never have to ask twice.
 */
export const FEE_PAYMENT_STATUSES = ['settled', 'refunded'] as const;

export const FeePaymentStatusSchema = z.enum(FEE_PAYMENT_STATUSES);

export type FeePaymentStatus = (typeof FEE_PAYMENT_STATUSES)[number];

/**
 * One settled (or later refunded) fee, as billing's ledger records it (D9a).
 *
 * `externalRef` is the idempotency handle per door — the RevenueCat
 * `transaction_id` for a store purchase, the order id for a direct one — and
 * `(store, externalRef)` is unique in the table, which is what makes a webhook
 * replay a no-op rather than a second ticket (N14's `billing_events` discipline,
 * applied one table further down).
 *
 * `amount`/`currency` are what the door **reported**, not the catalogue price:
 * a store purchase carries the storefront's figures, a direct order the
 * gender-resolved quote. Both nullable, because RevenueCat's sandbox events omit
 * them and a fee that was paid is a fee that was paid.
 */
export const FeePaymentSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,
    productKey: ProductKeySchema,
    purpose: FeePurposeSchema,
    store: EntitlementStoreSchema,
    externalRef: z.string().min(1),
    amount: MoneyAmountSchema.nullable(),
    currency: ReportedCurrencySchema.nullable(),
    status: FeePaymentStatusSchema,
    settledAt: z.iso.datetime(),
    refundedAt: z.iso.datetime().nullable(),
  })
  .strict();

export type FeePayment = z.infer<typeof FeePaymentSchema>;

/**
 * What a member is offered when a fee is due — the paywall-shaped answer to
 * "how do I pay for this?".
 *
 * `amount` is the **direct** price at the member's own scope (U15: resolved by
 * the registry, never branched on here); the store ids are what the phone hands
 * to RevenueCat. Either door may be closed for a deployment (no direct price, or
 * no store product), and the shape says so with `null` rather than inventing a
 * figure.
 */
export const FeeOfferSchema = z
  .object({
    productKey: ProductKeySchema,
    purpose: FeePurposeSchema,
    /** The caller's locale. */
    name: z.string(),
    description: z.string(),
    amount: MoneyAmountSchema.nullable(),
    currency: BillingCurrencySchema,
    iosProductId: z.string().nullable(),
    androidProductId: z.string().nullable(),
  })
  .strict();

export type FeeOffer = z.infer<typeof FeeOfferSchema>;
